import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  StarRate as StarRateIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
  CallSplit as CallSplitIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import api, { communityAPI } from '../services/api';
import { getCurrentUser } from '../services/auth';

const buildAuthorName = (author) => {
  if (!author) {
    return 'Unknown author';
  }
  const first = author.firstName || '';
  const last = author.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || author.email || 'Unknown author';
};

const buildBadges = (caseItem) => {
  const badges = [];
  const stats = caseItem?.community?.stats || {};
  const moderation = caseItem?.community?.moderation || {};

  if ((stats.popularityScore || 0) >= 3) {
    badges.push('Popular');
  }
  if ((stats.completionRate || 0) >= 0.7) {
    badges.push('High completion rate');
  }
  const approvedRole = (caseItem?.community?.approvedByRole || '').toLowerCase();
  if (approvedRole.includes('admin') || approvedRole.includes('instructor')) {
    badges.push('Faculty reviewed');
  }
  const updatedAt = caseItem?.updatedAt || caseItem?.community?.publishedAt || caseItem?.createdAt;
  if (updatedAt) {
    const updatedDate = new Date(updatedAt);
    const diffDays = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 7) {
      badges.push('Updated recently');
    }
  }
  if (moderation.flagged) {
    badges.push('Reported');
  }

  return badges;
};

const formatRelativeDate = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return formatDistanceToNow(parsed, { addSuffix: true });
};

const normalizePresetPayload = (payload) => {
  const source = payload || {};
  if (Array.isArray(source.items)) {
    return source.items;
  }
  if (Array.isArray(source.all)) {
    return source.all;
  }
  return Array.isArray(source) ? source : [];
};

const resolveCommunityOriginId = (caseItem) => {
  const provenance = caseItem?.provenance || {};
  const originId = provenance.originCaseId || provenance.sourceCaseId || caseItem?.originCaseId || caseItem?.sourceCaseId;
  return originId ? originId.toString() : '';
};

const buildImportMap = (cases = []) => {
  const map = {};
  cases.forEach((caseItem) => {
    const originId = resolveCommunityOriginId(caseItem);
    if (!originId) {
      return;
    }
    const lastOriginVersion = Number(
      caseItem?.provenance?.lastOriginVersion ?? caseItem?.provenance?.sourceVersion ?? caseItem?.sourceVersion ?? 0
    );
    const importedAt = new Date(caseItem?.createdAt || caseItem?.updatedAt || 0).getTime() || 0;
    const existing = map[originId] || {
      count: 0,
      latestCopyId: caseItem._id,
      latestOriginVersion: lastOriginVersion,
      latestImportedAt: importedAt,
      copyIds: [],
    };
    existing.count += 1;
    existing.copyIds.push(caseItem._id);
    if (importedAt > existing.latestImportedAt) {
      existing.latestCopyId = caseItem._id;
      existing.latestImportedAt = importedAt;
    }
    existing.latestOriginVersion = Math.max(existing.latestOriginVersion || 0, lastOriginVersion || 0);
    map[originId] = existing;
  });
  return map;
};

const CommunityLibraryPage = () => {
  const navigate = useNavigate();
  const user = useMemo(() => getCurrentUser(), []);
  const isUniversityUser = user?.accountType === 'university';

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    scope: isUniversityUser ? 'tenant' : 'global',
    presentingComplaint: 'all',
    specialty: 'all',
    difficulty: 'all',
    sortBy: 'newest'
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [cases, setCases] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [presets, setPresets] = useState({ presentingComplaints: [], specialties: [] });
  const [actionState, setActionState] = useState({ caseId: null, action: null });
  const [importedMap, setImportedMap] = useState({});
  const [importedLoading, setImportedLoading] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters, selectedTags, searchTerm]);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const [complaintsRes, specialtiesRes] = await Promise.all([
          api.get('/platform/presets/presenting-complaints'),
          api.get('/platform/presets/specialties')
        ]);
        setPresets({
          presentingComplaints: normalizePresetPayload(complaintsRes.data),
          specialties: normalizePresetPayload(specialtiesRes.data)
        });
      } catch (err) {
        console.error('Failed to load presets', err);
      }
    };

    loadPresets();
  }, []);

  const refreshImports = useCallback(async () => {
    if (!user) {
      return;
    }
    setImportedLoading(true);
    try {
      const response = await api.get('/cases', {
        params: {
          library: 'personal',
          originType: 'community',
          limit: 1000,
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        }
      });
      const importedCases = response.data?.cases || [];
      setImportedMap(buildImportMap(importedCases));
    } catch (err) {
      console.error('Failed to load community imports', err);
    } finally {
      setImportedLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshImports();
  }, [refreshImports]);

  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      setError('');

      const params = {
        scope: filters.scope,
        search: searchTerm || undefined,
        presentingComplaint: filters.presentingComplaint !== 'all' ? filters.presentingComplaint : undefined,
        specialty: filters.specialty !== 'all' ? filters.specialty : undefined,
        difficulty: filters.difficulty !== 'all' ? filters.difficulty : undefined,
        tags: selectedTags.length ? selectedTags.join(',') : undefined,
        sortBy: filters.sortBy,
        sortOrder: 'desc',
        page: pagination.page,
        limit: 20
      };

      try {
        const response = await communityAPI.listCases(params);
        setCases(response.data?.cases || []);
        setPagination(response.data?.pagination || { page: 1, pages: 1, total: 0 });
      } catch (err) {
        const message = err.response?.data?.message || 'Unable to load community cases.';
        if (filters.scope === 'tenant' && err.response?.status === 403) {
          setFilters((prev) => ({ ...prev, scope: 'global' }));
          setSnackbar({ open: true, message: 'Tenant community is not available. Showing global listings instead.', severity: 'info' });
        } else {
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [filters, selectedTags, searchTerm, pagination.page]);

  const tagOptions = useMemo(() => {
    const tags = cases.flatMap((caseItem) => caseItem.tags || []);
    return Array.from(new Set(tags)).sort();
  }, [cases]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleTagChange = (_event, value) => {
    const normalized = Array.from(new Set(
      (value || []).map((tag) => (typeof tag === 'string' ? tag.trim() : tag?.name || '').trim()).filter(Boolean)
    ));
    setSelectedTags(normalized);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleImport = async (caseId) => {
    setActionState({ caseId, action: 'import' });
    try {
      await communityAPI.importCase(caseId);
      setSnackbar({ open: true, message: 'Case imported to your personal library.', severity: 'success' });
      refreshImports();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to import this case.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setActionState({ caseId: null, action: null });
    }
  };

  const handleFork = async (caseId) => {
    setActionState({ caseId, action: 'fork' });
    try {
      await communityAPI.forkCase(caseId);
      setSnackbar({ open: true, message: 'Case forked to your personal library.', severity: 'success' });
      refreshImports();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to fork this case.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setActionState({ caseId: null, action: null });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Community Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Explore community-created cases. Import a copy into your library to edit or practice in the Virtual Medic app.
          </Typography>
        </Box>
        <Alert severity="info">
          Community listings are created by learners and clinicians. Imported copies are yours to edit; the originals remain unchanged.
        </Alert>
        {importedLoading && (
          <Typography variant="caption" color="text.secondary">
            Checking your library for existing imports...
          </Typography>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Search by title, complaint, specialty, or tags"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort by"
                onChange={handleFilterChange('sortBy')}
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="popular">Most popular</MenuItem>
                <MenuItem value="rating">Highest rated</MenuItem>
                <MenuItem value="imports">Most imported</MenuItem>
                <MenuItem value="forks">Most forked</MenuItem>
                <MenuItem value="updated">Recently updated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              value={selectedTags}
              onChange={handleTagChange}
              renderInput={(params) => (
                <TextField {...params} label="Filter by tags" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Presenting complaint</InputLabel>
              <Select
                value={filters.presentingComplaint}
                label="Presenting complaint"
                onChange={handleFilterChange('presentingComplaint')}
              >
                <MenuItem value="all">All complaints</MenuItem>
                {presets.presentingComplaints.map((complaint) => (
                  <MenuItem key={complaint._id || complaint.name} value={complaint.name}>
                    {complaint.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Specialty</InputLabel>
              <Select
                value={filters.specialty}
                label="Specialty"
                onChange={handleFilterChange('specialty')}
              >
                <MenuItem value="all">All specialties</MenuItem>
                {presets.specialties.map((specialty) => (
                  <MenuItem key={specialty._id || specialty.name} value={specialty.name}>
                    {specialty.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={filters.difficulty}
                label="Difficulty"
                onChange={handleFilterChange('difficulty')}
              >
                <MenuItem value="all">All levels</MenuItem>
                <MenuItem value="Core">Core</MenuItem>
                <MenuItem value="Intermediate">Intermediate</MenuItem>
                <MenuItem value="Advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {isUniversityUser && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={filters.scope}
                  label="Scope"
                  onChange={handleFilterChange('scope')}
                >
                  <MenuItem value="tenant">University community</MenuItem>
                  <MenuItem value="global">Global community</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {cases.map((caseItem) => {
            const stats = caseItem.community?.stats || {};
            const badges = buildBadges(caseItem);
            const ratingValue = Number(stats.averageRating || 0).toFixed(1);
            const ratingCount = stats.ratingCount || 0;
            const updatedLabel = formatRelativeDate(caseItem.updatedAt || caseItem.community?.publishedAt);
            const isBusy = actionState.caseId === caseItem._id;
            const importInfo = importedMap[caseItem._id];
            const hasImported = Boolean(importInfo);
            const communityVersion = Number(caseItem.version || 0);
            const hasUpdate = hasImported
              && communityVersion
              && Number(importInfo.latestOriginVersion || 0) < communityVersion;
            const importLabel = hasUpdate
              ? 'Import update'
              : (hasImported ? 'Import another copy' : 'Import');

            return (
              <Paper key={caseItem._id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Box>
                      <Typography variant="h6">{caseItem.title}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">
                          By {buildAuthorName(caseItem.author)}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <StarRateIcon fontSize="small" color="warning" />
                          <Typography variant="body2">{ratingValue} ({ratingCount})</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="body2" color="text.secondary">
                          Updated {updatedLabel}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {caseItem.presentingComplaint} - {caseItem.specialty} - {caseItem.difficulty}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {hasImported && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/cases/${importInfo.latestCopyId}`)}
                        >
                          Open latest copy
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/community-library/${caseItem._id}`)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        disabled={isBusy && actionState.action === 'import'}
                        onClick={() => handleImport(caseItem._id)}
                      >
                        {importLabel}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<CallSplitIcon />}
                        disabled={isBusy && actionState.action === 'fork'}
                        onClick={() => handleFork(caseItem._id)}
                      >
                        Fork
                      </Button>
                    </Stack>
                  </Stack>

                  {caseItem.shortDescription && (
                    <Typography variant="body2" color="text.secondary">
                      {caseItem.shortDescription}
                    </Typography>
                  )}

                  {Array.isArray(caseItem.tags) && caseItem.tags.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {caseItem.tags.map((tag) => (
                        <Chip key={`${caseItem._id}-${tag}`} label={tag} size="small" />
                      ))}
                    </Stack>
                  )}

                  {badges.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {badges.map((badge) => (
                        <Chip key={`${caseItem._id}-${badge}`} label={badge} size="small" color="info" variant="outlined" />
                      ))}
                    </Stack>
                  )}
                  {hasImported && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        label={`In your library${importInfo.count > 1 ? ` (${importInfo.count})` : ''}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      {hasUpdate && (
                        <Chip label="Update available" size="small" color="warning" variant="outlined" />
                      )}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            );
          })}

          {!cases.length && !loading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1">No community cases match these filters yet.</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(_event, page) => setPagination((prev) => ({ ...prev, page }))}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunityLibraryPage;
